import { useQueries, useMutation } from '@tanstack/react-query';
import { useState, useCallback } from 'react';

import type { MutationConfig, QueryConfig } from '@/lib/react-query';
import { useNotifications } from '@/stores/notifications';

import { SELECTION_OPTIONS } from '../constants';

import { assignStudent } from './assign-student';
import {
  Batch,
  getAvailableBatches,
  type GetAvailableBatchesParams,
  type OngoingVoucher,
} from './get-available-batches';
import { joinWaitlist } from './join-waitlist';

// Query Keys for Batch Selection
export const batchSelectionKeys = {
  all: ['batch-selection'] as const,
  batches: () => [...batchSelectionKeys.all, 'batches'] as const,
  availableBatches: (params: GetAvailableBatchesParams) =>
    [...batchSelectionKeys.batches(), 'available', params] as const,
} as const;

// Helper for inferring the query type
const _getAvailableBatchesQuery = (params: GetAvailableBatchesParams) => ({
  queryKey: batchSelectionKeys.availableBatches(params),
  queryFn: () => getAvailableBatches(params),
});

// Types for multiple variants
export interface VariantInfo {
  variantId: string;
  courseName: string;
}

export interface UseGetAvailableBatchesParams {
  profileId: string;
  variants: VariantInfo[];
  country: string;
  queryConfig?: Partial<QueryConfig<typeof _getAvailableBatchesQuery>>;
}

// Configuration constants for retry logic
const MAX_RETRIES = 3;
const INITIAL_RETRY_DELAY = 1000; // 1 second
const MAX_RETRY_DELAY = 30_000; // 30 seconds

// Generic hook for handling single or multiple variant API calls
export const useGetAvailableBatches = ({
  profileId,
  variants,
  country,
  queryConfig,
}: UseGetAvailableBatchesParams) => {
  // Create queries for each variant
  const queries = variants.map(variant => ({
    queryKey: batchSelectionKeys.availableBatches({
      profile_id: profileId,
      variant_id: variant.variantId,
      country,
    }),
    queryFn: () =>
      getAvailableBatches({
        profile_id: profileId,
        variant_id: variant.variantId,
        country,
      }),
    enabled: !!(profileId && variant.variantId && country),
    retry: (failureCount: number, error: any) => {
      // Don't retry 404s (data doesn't exist), but retry network errors
      if ((error as { status?: number })?.status === 404) return false;
      // Cap retries at MAX_RETRIES
      return failureCount < MAX_RETRIES;
    },
    retryDelay: (attemptIndex: number) =>
      Math.min(INITIAL_RETRY_DELAY * 2 ** attemptIndex, MAX_RETRY_DELAY),
    ...queryConfig,
  }));

  const results = useQueries({
    queries,
  });

  // Process results
  const isLoading = results.some(result => result.isLoading);
  const hasError = results.some(result => result.error);
  const allSuccess = results.every(result => result.isSuccess);

  // Get variants by status for better UX
  const loadingVariants = variants.filter(
    (_, index) => results[index].isLoading
  );
  const errorVariants = variants.filter((_, index) => results[index].error);
  const successVariants = variants.filter(
    (_, index) => results[index].isSuccess
  );

  // Transform data to CourseWiseBatchesData format
  const courseWiseBatchesData: Record<
    string,
    { recommended_batches: Batch[]; other_batches: Batch[]; status: string }
  > = {};
  let ongoingVoucher: OngoingVoucher | undefined;
  for (const [index, result] of results.entries()) {
    const variant = variants[index];
    if (result.data) {
      courseWiseBatchesData[variant.courseName] = {
        recommended_batches: result.data.recommended_batches || [],
        other_batches: result.data.other_batches || [],
        status: result.data?.status,
      };
      if (!ongoingVoucher && result.data.ongoing_voucher) {
        ongoingVoucher = result.data.ongoing_voucher;
      }
    }
  }

  // Progress tracking
  const completedCount = results.filter(result => !result.isLoading).length;
  const progress = {
    completed: completedCount,
    total: variants.length,
    percentage:
      variants.length > 0
        ? Math.round((completedCount / variants.length) * 100)
        : 0,
  };

  // Refetch all queries
  const refetch = () => {
    for (const result of results) {
      void result.refetch();
    }
  };

  return {
    courseWiseBatchesData,
    ongoingVoucher,
    isLoading,
    hasError,
    allSuccess,
    loadingVariants,
    errorVariants,
    successVariants,
    progress,
    refetch,
    results, // Raw results for advanced use cases
  };
};

// Mutation hook for assigning student to batch
export const useAssignStudent = (
  mutationConfig?: MutationConfig<typeof assignStudent>
) => {
  return useMutation({
    mutationFn: assignStudent,
    ...mutationConfig,
  });
};

// Types for multiple batch assignments
export interface BatchAssignment {
  profileId: string; // profile.id - for waitlist API
  leadId: string; // profile.base_lead_id - for batch assignment API
  variantId: string;
  batchId: string;
  courseName?: string; // Optional for better UX tracking
  country: string; // Required for waitlist API
}

export interface UseAssignMultipleBatchesResult {
  assignBatches: (
    assignments: BatchAssignment[],
    selectedWaitlistReason: string
  ) => Promise<void>;
  isLoading: boolean;
  progress: {
    completed: number;
    total: number;
    percentage: number;
  };
  results: {
    successful: BatchAssignment[];
    failed: Array<{ assignment: BatchAssignment; error: any }>;
  };
  reset: () => void;
}

// Hook for handling multiple batch assignments
export const useAssignMultipleBatches = (): UseAssignMultipleBatchesResult => {
  const { addNotification } = useNotifications();
  const [isLoading, setIsLoading] = useState(false);
  const [progress, setProgress] = useState({
    completed: 0,
    total: 0,
    percentage: 0,
  });
  const [results, setResults] = useState<{
    successful: BatchAssignment[];
    failed: Array<{ assignment: BatchAssignment; error: any }>;
  }>({
    successful: [],
    failed: [],
  });

  const reset = useCallback(() => {
    setIsLoading(false);
    setProgress({ completed: 0, total: 0, percentage: 0 });
    setResults({ successful: [], failed: [] });
  }, []);

  const assignBatches = useCallback(
    async (assignments: BatchAssignment[], selectedWaitlistReason: string) => {
      if (assignments.length === 0) return;

      setIsLoading(true);
      setProgress({ completed: 0, total: assignments.length, percentage: 0 });
      setResults({ successful: [], failed: [] });

      const successful: BatchAssignment[] = [];
      const failed: Array<{ assignment: BatchAssignment; error: any }> = [];

      // Process assignments in parallel (like useQueries)
      const promises = assignments.map(async assignment => {
        try {
          // Check if this is a waitlist selection or regular batch assignment
          await (assignment.batchId === SELECTION_OPTIONS.WAITLIST
            ? joinWaitlist({
                profile_id: assignment.profileId,
                variant_id: assignment.variantId,
                country: assignment.country,
                metadata: {
                  waitlist_reason: selectedWaitlistReason,
                },
              })
            : assignStudent({
                lead_id: assignment.profileId,
                variant_id: assignment.variantId,
                batch_id: assignment.batchId,
              }));
          successful.push(assignment);
          return { success: true, assignment };
        } catch (error) {
          failed.push({ assignment, error });
          addNotification({
            type: 'error',
            title: 'Error: ',
            message:
              (error as Error).message ??
              'Something went wrong. Please try again.',
          });
          return { success: false, assignment, error };
        }
      });

      // Wait for all assignments to complete and update progress
      const results = await Promise.allSettled(promises);

      for (const [index] of results.entries()) {
        const completedCount = index + 1;
        setProgress({
          completed: completedCount,
          total: assignments.length,
          percentage: Math.round((completedCount / assignments.length) * 100),
        });
      }

      setResults({ successful, failed });
      setIsLoading(false);
    },
    [addNotification]
  );

  return {
    assignBatches,
    isLoading,
    progress,
    results,
    reset,
  };
};
